// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME, PRODUCT_NAME, VOICE_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Select} from '@app/features/ui/components/form/Select';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Slider} from '@app/features/ui/components/Slider';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import PiP from '@app/features/ui/state/PiP';
import {CodecSelectorSection} from '@app/features/user/components/modals/tabs/components/CodecSelectorSection';
import {useMediaPermission} from '@app/features/user/components/modals/tabs/hooks/useMediaPermission';
import styles from '@app/features/user/components/modals/tabs/VideoTab.module.css';
import PrivacyPreferences from '@app/features/user/state/PrivacyPreferences';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import {CameraPreviewModalStandalone} from '@app/features/voice/components/modals/CameraPreviewModal';
import type VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {ScreenshareResolution} from '@app/features/voice/state/VoiceSettings';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo} from 'react';

interface VideoTabProps {
	voiceSettings: typeof VoiceSettings;
	hasPremium: boolean;
	autoRequestPermission?: boolean;
}
export const VideoTab: React.FC<VideoTabProps> = observer(
	({voiceSettings, hasPremium: _hasPremium, autoRequestPermission = true}) => {
		const {t} = useLingui();
		const {
			videoDeviceId,
			cameraResolution,
			screenshareResolution,
			videoFrameRate,
			pauseOwnScreenSharePreviewOnUnfocus,
			disablePictureInPicturePopout,
			disablePictureInPicturePopoutScreenShare,
			streamHideCursor,
		} = voiceSettings;
		const disableStreamPreviews = PrivacyPreferences.getDisableStreamPreviews();
		const hasHigherQuality = useMemo(
			() =>
				isLimitToggleEnabled(
					{
						feature_higher_video_quality: LimitResolver.resolve({
							key: 'feature_higher_video_quality',
							fallback: 0,
						}),
					},
					'feature_higher_video_quality',
				),
			[],
		);
		const {
			devices,
			status: permissionStatus,
			requestPermission,
		} = useMediaPermission('video', {
			autoRequest: autoRequestPermission,
		});
		useEffect(() => {
			if (videoDeviceId === 'default' && devices.length > 0) {
				VoiceSettingsCommands.update({videoDeviceId: devices[0].deviceId});
			}
		}, [devices, videoDeviceId]);
		const videoDeviceOptions =
			devices.length > 0
				? devices.map((device) => {
						const shortDeviceId = device.deviceId.slice(0, 8);
						return {
							value: device.deviceId,
							label: device.label || t`Camera ${shortDeviceId}`,
						};
					})
				: [{value: 'default', label: t`Default`}];
		const effectiveVideoDeviceId = devices.length === 0 ? 'default' : videoDeviceId;
		const cameraResolutionOptions: ReadonlyArray<RadioOption<'low' | 'medium' | 'high'>> = [
			{value: 'low', name: t`Low (480p)`, desc: t`Best for slower connections`},
			{value: 'medium', name: t`Medium (720p)`, desc: t`Balanced quality and bandwidth`},
			{value: 'high', name: t`High (1080p)`, desc: t`Best quality for camera`},
		];
		const screenshareResolutionOptions: ReadonlyArray<RadioOption<ScreenshareResolution>> = [
			{value: 'low_240p', name: t`Very Low (240p)`, desc: t`Best for slower connections`},
			{value: 'low_480p', name: t`Low (480p)`, desc: t`Best for slower connections`},
			{value: 'medium', name: t`Standard (720p)`, desc: t`Balanced quality and bandwidth`},
			{
				value: 'high',
				name: t`High Definition (1080p)`,
				desc: hasHigherQuality
					? t`Best quality for most users`
					: !RuntimeConfig.isSelfHosted()
						? t`Requires ${PREMIUM_PRODUCT_NAME}`
						: t`Not available`,
				disabled: !hasHigherQuality && !RuntimeConfig.isSelfHosted(),
			},
			{
				value: 'ultra',
				name: t`Quad HD (1440p)`,
				desc: hasHigherQuality
					? t`High quality, requires fast connection`
					: !RuntimeConfig.isSelfHosted()
						? t`Requires ${PREMIUM_PRODUCT_NAME}`
						: t`Not available`,
				disabled: !hasHigherQuality && !RuntimeConfig.isSelfHosted(),
			},
			{
				value: 'source',
				name: t`Source (native)`,
				desc: hasHigherQuality
					? t`Capture at the source's own resolution with no downscaling`
					: !RuntimeConfig.isSelfHosted()
						? t`Requires ${PREMIUM_PRODUCT_NAME}`
						: t`Not available`,
				disabled: !hasHigherQuality && !RuntimeConfig.isSelfHosted(),
			},
		];
		const handleCameraPreview = async () => {
			const granted = await requestPermission();
			if (granted) {
				ModalCommands.push(modal(() => <CameraPreviewModalStandalone showEnableCameraButton={false} />));
			}
		};
		const handleDisableStreamPreviewToggle = useCallback((value: boolean) => {
			PrivacyPreferences.setDisableStreamPreviews(value);
		}, []);
		const handleDisablePopoutToggle = useCallback((value: boolean) => {
			VoiceSettingsCommands.update({disablePictureInPicturePopout: value});
			if (!value) {
				PiP.setSessionDisable(false);
			}
		}, []);
		const handleDisableScreenSharePopoutToggle = useCallback((value: boolean) => {
			VoiceSettingsCommands.update({disablePictureInPicturePopoutScreenShare: value});
			if (!value) {
				PiP.setSessionDisable(false);
			}
		}, []);
		const handleScreenshareResolutionChange = useCallback((value: ScreenshareResolution) => {
			VoiceSettingsCommands.update({screenshareResolution: value, streamingMode: 'custom'});
		}, []);
		const handleVideoFrameRateChange = useCallback((value: number) => {
			VoiceSettingsCommands.update({videoFrameRate: value, streamingMode: 'custom'});
		}, []);
		return (
			<>
				{devices.length === 0 && permissionStatus !== 'loading' && permissionStatus !== 'granted' ? (
					<div className={styles.deviceNotice}>
						<div className={styles.deviceNoticeText}>
							<div className={styles.deviceNoticeTitle}>
								<Trans>No cameras detected</Trans>
							</div>
							<p className={styles.deviceNoticeDescription}>
								{permissionStatus === 'denied' ? (
									<Trans>
										Allow {PRODUCT_NAME} to access your camera in System Settings → Privacy &amp; Security → Camera to
										preview and select devices.
									</Trans>
								) : (
									<Trans>{PRODUCT_NAME} needs access to your camera before we can list it here.</Trans>
								)}
							</p>
						</div>
						<Button
							variant="secondary"
							small={true}
							onClick={() => {
								void requestPermission();
							}}
						>
							<Trans>Allow Camera</Trans>
						</Button>
					</div>
				) : null}
				<div>
					<Select
						label={t`Camera`}
						value={effectiveVideoDeviceId}
						options={videoDeviceOptions}
						onChange={(value) => VoiceSettingsCommands.update({videoDeviceId: value})}
					/>
				</div>
				<Button variant="primary" fitContainer={false} className={styles.actionButton} onClick={handleCameraPreview}>
					<Trans>Start Camera Test &amp; Configure Effects</Trans>
				</Button>
				<div>
					<div className={styles.sectionTitle}>
						<Trans>Camera Quality</Trans>
					</div>
					<p className={styles.sectionDescription}>
						<Trans>Maximum quality for camera is 1080p</Trans>
					</p>
					<RadioGroup
						aria-label={t`Camera Quality`}
						options={cameraResolutionOptions}
						value={cameraResolution}
						onChange={(value) => VoiceSettingsCommands.update({cameraResolution: value})}
					/>
				</div>
				<div>
					<div className={styles.sectionTitle}>
						<Trans>Screen Sharing Quality</Trans>
					</div>
					<RadioGroup
						aria-label={t`Screen Sharing Quality`}
						options={screenshareResolutionOptions}
						value={screenshareResolution}
						onChange={handleScreenshareResolutionChange}
					/>
				</div>
				<div>
					<div className={styles.sectionTitle}>
						<Trans>Screen Sharing Codec</Trans>
					</div>
					<p className={styles.sectionDescription}>
						<Trans>
							Choose the video codec {PRODUCT_NAME} uses when streaming your screen in voice channels.
							&ldquo;Auto&rdquo; picks the best one your browser, system, and the {VOICE_PROVIDER_NAME} voice server all
							support. Changes apply next time you start sharing your screen.
						</Trans>
					</p>
					<CodecSelectorSection />
				</div>
				<Switch
					label={<Trans>Pause my screen share preview when unfocused</Trans>}
					description={<Trans>Freezes your preview while {PRODUCT_NAME} is in the background to save resources.</Trans>}
					value={pauseOwnScreenSharePreviewOnUnfocus}
					onChange={(value) => VoiceSettingsCommands.update({pauseOwnScreenSharePreviewOnUnfocus: value})}
				/>
				<Switch
					label={<Trans>Disable Picture-in-Picture Popout for Calls</Trans>}
					description={
						<Trans>
							Prevents the floating pop-out from appearing when you navigate to another part of the application while
							focused on a camera stream during a call. Enabled by default.
						</Trans>
					}
					value={disablePictureInPicturePopout}
					onChange={handleDisablePopoutToggle}
				/>
				<Switch
					label={<Trans>Disable Picture-in-Picture Popout for Screen Shares</Trans>}
					description={
						<Trans>
							Prevents the floating pop-out from appearing when you navigate to another part of the application while
							watching someone's screen share.
						</Trans>
					}
					value={disablePictureInPicturePopoutScreenShare}
					onChange={handleDisableScreenSharePopoutToggle}
				/>
				<Switch
					label={<Trans>Disable Stream Previews</Trans>}
					description={
						<Trans>
							When enabled, other people will not see a thumbnail preview of your screen share. Your stream content is
							still visible to anyone watching.
						</Trans>
					}
					value={disableStreamPreviews}
					onChange={handleDisableStreamPreviewToggle}
				/>
				<Switch
					label={<Trans>Show cursor when streaming</Trans>}
					description={
						<Trans>
							When enabled, viewers will see your mouse cursor in your screen share. Takes effect the next time you
							start a screen share.
						</Trans>
					}
					value={!streamHideCursor}
					onChange={(value) => VoiceSettingsCommands.update({streamHideCursor: !value})}
				/>
				{!hasHigherQuality && !RuntimeConfig.isSelfHosted() && (
					<div className={styles.premiumCard}>
						<div className={styles.premiumHeader}>
							<CrownIcon weight="fill" size={18} className={styles.premiumIcon} />
							<span className={styles.premiumTitle}>
								<Trans>Unlock HD Screen Sharing with {PREMIUM_PRODUCT_NAME}</Trans>
							</span>
						</div>
						<p className={styles.premiumDescription}>
							<Trans>
								Get crystal clear screen sharing with High Definition (1080p), Quad HD (1440p), and native Source
								resolutions, plus unlock frame rates up to 60 FPS for the smoothest experience.
							</Trans>
						</p>
						<Button variant="secondary" small={true} onClick={() => PremiumModalCommands.open()}>
							<Trans>Get {PREMIUM_PRODUCT_NAME}</Trans>
						</Button>
					</div>
				)}
				<div>
					<div className={styles.sectionTitle}>
						<Trans>Frame Rate</Trans>
					</div>
					<p className={styles.sectionDescription}>
						<Trans>Applies to both camera and screen sharing</Trans>
					</p>
					<Slider
						defaultValue={videoFrameRate}
						factoryDefaultValue={30}
						minValue={15}
						maxValue={hasHigherQuality ? 60 : 30}
						markers={hasHigherQuality ? [15, 30, 60] : [15, 30]}
						stickToMarkers={true}
						onMarkerRender={(value) => `${Math.round(value)}FPS`}
						onValueChange={handleVideoFrameRateChange}
						showResetButton={true}
					/>
					{!hasHigherQuality && !RuntimeConfig.isSelfHosted() && (
						<div className={styles.frameRateNote}>
							<CrownIcon weight="fill" size={14} className={styles.frameRateIcon} />
							<Trans>Frame rates above 30 FPS require {PREMIUM_PRODUCT_NAME}</Trans>
						</div>
					)}
				</div>
			</>
		);
	},
);
